/**
 * The per-recipient worklist, filtered by status. `Failed` is the view people
 * actually want: who did not get their envelope, and why.
 *
 * The design draws a "corrected contact" input and a per-row Retry on this
 * table. Neither exists on the server — retry requeues the failed rows as they
 * are, and there is no call to edit a row — so the column is left out rather
 * than shipped dead, and the footer says how a correction is actually made.
 * Flagged to backend; when row edits land this table gains the column.
 */

import React from "react";
import { Box, Skeleton } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import EmptyState from "global/components/EmptyState/EmptyState";
import { batchStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import { BulkBatchRow, BulkRowStatus } from "../BulkSendTypesAndValidation";
import { roleInitials } from "../BulkSendHelper";
import { InfoIcon } from "./BulkSendIcons";

const p = tokens.palette;
const t = tokens.batch;
const s = batchStyles;

export type RowFilter = BulkRowStatus | "All";

const FILTERS: { key: RowFilter; label: string }[] = [
  { key: "Failed", label: "Needs fixing" },
  { key: "Pending", label: "Queued" },
  { key: "Sent", label: "Sent" },
  { key: "Cancelled", label: "Cancelled" },
  { key: "All", label: "All" },
];

interface Props {
  rows: BulkBatchRow[];
  isLoading: boolean;
  filter: RowFilter;
  counts: Partial<Record<BulkRowStatus, number>>;
  totalRows: number;
  onFilter: (filter: RowFilter) => void;
}

/** A row's first recipient is the one the sender recognises it by. */
const primaryRecipient = (row: BulkBatchRow) => row.recipients?.[0];

const BatchRowsTable: React.FC<Props> = ({
  rows,
  isLoading,
  filter,
  counts,
  totalRows,
  onFilter,
}) => {
  const countFor = (key: RowFilter) =>
    key === "All" ? totalRows : (counts[key] ?? 0);

  return (
    <>
      <Box
        sx={s.filters}
        role="tablist"
        aria-label="Filter recipients by status"
      >
        {FILTERS.map((option) => {
          const count = countFor(option.key);
          if (count === 0 && option.key !== filter && option.key !== "All") {
            return null;
          }
          return (
            <Box
              key={option.key}
              component="button"
              type="button"
              role="tab"
              aria-selected={filter === option.key}
              onClick={() => onFilter(option.key)}
              sx={s.filter(filter === option.key)}
            >
              {option.label}
              <Box component="span" sx={s.filterCount}>
                {count}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={s.tableHeader} role="row">
        <Box component="span">Recipient</Box>
        <Box component="span">What happened</Box>
        <Box component="span">Status</Box>
        <Box component="span" />
      </Box>

      {isLoading ? (
        [0, 1, 2, 3].map((key) => (
          <Box key={key} sx={s.tableRow}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Skeleton variant="circular" sx={s.tableAvatar("neutral")} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  variant="text"
                  sx={[s.tableName, { width: "52%" }] as SxProps<Theme>}
                />
                <Skeleton
                  variant="text"
                  sx={[s.tableMeta, { width: "72%" }] as SxProps<Theme>}
                />
              </Box>
            </Box>
            <Skeleton variant="text" sx={{ width: "80%" }} />
            <Skeleton variant="text" sx={{ width: "40%" }} />
            <Box />
          </Box>
        ))
      ) : rows.length === 0 ? (
        <Box sx={{ p: t.tableRowPadding }}>
          <EmptyState
            size="medium"
            icon={filter === "Failed" ? "activity" : "inbox"}
            title={
              filter === "Failed"
                ? "Nothing failed"
                : "No recipients in this view"
            }
            body={
              filter === "Failed"
                ? "Every recipient in this batch was sent their copy."
                : "Try another filter to see the rest of the batch."
            }
          />
        </Box>
      ) : (
        rows.map((row) => {
          const recipient = primaryRecipient(row);
          const hasFailed = row.status === "Failed";
          return (
            <Box
              key={`${row.rowNumber}-${row.envelopeId ?? ""}`}
              sx={s.tableRow}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  minWidth: 0,
                }}
              >
                <Box sx={s.tableAvatar(hasFailed ? "danger" : "neutral")}>
                  {roleInitials(recipient?.name || recipient?.email || "?")}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box component="span" sx={s.tableName}>
                    {recipient?.name || recipient?.email || "Unnamed recipient"}
                  </Box>
                  <Box component="span" sx={s.tableMeta}>
                    Row {row.rowNumber}
                    {recipient?.email ? ` · ${recipient.email}` : ""}
                  </Box>
                </Box>
              </Box>

              <Box component="span" sx={s.tableMeta}>
                {row.errorMessage ||
                  (row.status === "Sent"
                    ? "Delivered"
                    : row.status === "Pending"
                      ? "Waiting its turn"
                      : "—")}
              </Box>

              <Box component="span">
                {hasFailed ? (
                  <Box component="span" sx={s.reason}>
                    {row.status}
                  </Box>
                ) : (
                  <Box component="span" sx={s.tableMeta}>
                    {row.status}
                  </Box>
                )}
              </Box>

              <Box />
            </Box>
          );
        })
      )}

      {rows.some((row) => row.status === "Failed") && (
        <Box sx={s.tableNote}>
          <InfoIcon size={15} color={p.body} />
          <Box component="span">
            Retrying resends these rows as they are. To change an address, fix
            it in your file and start a new batch.
          </Box>
        </Box>
      )}
    </>
  );
};

export default BatchRowsTable;
